import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, MouseEvent, TouchEvent } from 'react';
import { Download, Eraser, Loader2, RefreshCcw, Signature as SignatureIcon } from 'lucide-react';

import type { SignatureAnswerValue } from '@kushya/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { uploadFile } from '@/lib/api/upload';
import { cn } from '@/lib/utils';

import type { QuestionRendererProps } from '../types';

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 200;

const signaturePlaceholderClasses =
  'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 bg-muted/20 px-5 py-8 text-center';

function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0]?.match(/:(.*?);/)?.[1] ?? 'image/png';
  const bstr = atob(arr[1] ?? '');
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export const SignatureQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  sessionToken,
  ensureSessionToken,
  mode,
  disabled,
  error,
}: QuestionRendererProps<SignatureAnswerValue | string | null>) => {
  if (question.type !== 'signature') {
    return null;
  }

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const pendingTypedName = useMemo(() => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed && !trimmed.startsWith('http')) {
        return value;
      }
    }
    return undefined;
  }, [value]);

  const signatureValue = useMemo<SignatureAnswerValue | null>(() => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed && trimmed.startsWith('http')) {
        return { url: trimmed };
      }
      return null;
    }

    if (typeof value === 'object' && value !== null) {
      const record = value as SignatureAnswerValue;
      if (typeof record.url === 'string' && record.url.trim()) {
        return {
          url: record.url.trim(),
          ...(record.typedName ? { typedName: record.typedName } : {}),
          ...(record.uploadedAt ? { uploadedAt: record.uploadedAt } : {}),
        };
      }
    }

    return null;
  }, [value]);

  const signatureUrl = signatureValue?.url;
  const typedName = signatureValue?.typedName ?? pendingTypedName ?? '';

  const isPreview = mode === 'preview';
  const isInteractive = mode === 'runtime' && !disabled;
  const hasSavedSignature = Boolean(signatureUrl);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    canvas.style.width = '100%';
    canvas.style.height = `${CANVAS_HEIGHT}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f2937';
  }, []);

  const getCoordinates = useCallback((event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in event) {
      const touch = event.touches[0];
      if (!touch) return null;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  }, []);

  const startDrawing = useCallback(
    (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
      if (!isInteractive || isUploading || hasSavedSignature) {
        return;
      }

      const coords = getCoordinates(event);
      if (!coords) {
        return;
      }

      event.preventDefault();
      setLocalError(null);

      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      setIsDrawing(true);
    },
    [getCoordinates, hasSavedSignature, isInteractive, isUploading]
  );

  const draw = useCallback(
    (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing || !isInteractive || isUploading || hasSavedSignature) {
        return;
      }

      const coords = getCoordinates(event);
      if (!coords) {
        return;
      }

      event.preventDefault();

      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      setHasStrokes(true);
    },
    [getCoordinates, hasSavedSignature, isDrawing, isInteractive, isUploading]
  );

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const setSignature = useCallback(
    (url: string) => {
      if (!onChange) {
        return;
      }

      const payload: SignatureAnswerValue = {
        url,
        uploadedAt: new Date().toISOString(),
      };

      if (signatureValue?.typedName) {
        payload.typedName = signatureValue.typedName;
      } else if (pendingTypedName && pendingTypedName.trim()) {
        payload.typedName = pendingTypedName;
      }

      onChange(payload);
    },
    [onChange, pendingTypedName, signatureValue?.typedName]
  );

  const clearSignatureValue = useCallback(() => {
    if (!onChange) {
      return;
    }

    const preservedName = signatureValue?.typedName ?? pendingTypedName;
    if (preservedName && preservedName.trim()) {
      onChange(preservedName);
    } else {
      onChange(null);
    }

    setLocalError(null);
    setHasStrokes(false);
    clearCanvas();
    onBlur?.();
  }, [clearCanvas, onBlur, onChange, pendingTypedName, signatureValue?.typedName]);

  const handleSaveSignature = useCallback(async () => {
    if (!isInteractive || isUploading || hasSavedSignature) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes) {
      setLocalError('Please add your signature before saving.');
      return;
    }

    setIsUploading(true);
    setLocalError(null);

    try {
      const dataUrl = canvas.toDataURL('image/png');
      const file = dataUrlToFile(dataUrl, `signature-${Date.now()}.png`);
      const resolvedSessionToken = sessionToken ?? (await ensureSessionToken?.());
      if (!resolvedSessionToken) {
        throw new Error('Session token required');
      }
      const metadata = await uploadFile(file, { sessionToken: resolvedSessionToken });
      setSignature(metadata.url);
      clearCanvas();
      onBlur?.();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to upload signature.');
    } finally {
      setIsUploading(false);
    }
  }, [clearCanvas, ensureSessionToken, hasSavedSignature, hasStrokes, isInteractive, isUploading, onBlur, sessionToken, setSignature]);

  const handleTypedNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!isInteractive || !onChange) {
        return;
      }

      const nextValue = event.target.value;
      const trimmed = nextValue.trim();

      if (!signatureValue?.url) {
        onChange(trimmed ? nextValue : null);
        return;
      }

      const payload: SignatureAnswerValue = {
        url: signatureValue.url,
        ...(signatureValue.uploadedAt ? { uploadedAt: signatureValue.uploadedAt } : {}),
        ...(trimmed ? { typedName: nextValue } : {}),
      };

      onChange(payload);
    },
    [isInteractive, onChange, signatureValue?.uploadedAt, signatureValue?.url]
  );

  const showCanvas = !hasSavedSignature && mode === 'runtime';

  const validationDescriptor = useMemo(() => `validation-${question.id}`, [question.id]);
  const errorDescriptor = error ? `error-${question.id}` : undefined;

  if (isPreview) {
    return (
      <div className="space-y-3">
        <div className={signaturePlaceholderClasses}>
          <SignatureIcon className="size-10 text-muted-foreground" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Signature capture will be enabled in the live form</p>
            <p className="text-xs text-muted-foreground">Preview mode shows placeholder only.</p>
          </div>
          <div className="h-24 w-full rounded-md border border-border/60 bg-card" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {question.padTitle || question.padSubtitle ? (
        <div className="space-y-1">
          {question.padTitle ? <p className="text-sm font-semibold text-foreground">{question.padTitle}</p> : null}
          {question.padSubtitle ? <p className="text-xs text-muted-foreground">{question.padSubtitle}</p> : null}
        </div>
      ) : null}

      {showCanvas ? (
        <div className="space-y-3">
          <div
            className={cn(
              'relative rounded-lg border-2 border-dashed border-border/70 bg-white',
              !isInteractive && 'opacity-50'
            )}
          >
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              className={cn(
                'w-full touch-none bg-white',
                isInteractive ? 'cursor-crosshair' : 'cursor-not-allowed'
              )}
              aria-label="Signature pad"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              onTouchCancel={stopDrawing}
            />

            {!hasStrokes && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="text-sm text-muted-foreground">Sign your name in the box</span>
              </div>
            )}
          </div>

          {localError ? <p className="text-sm text-destructive">{localError}</p> : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                clearCanvas();
                setLocalError(null);
              }}
              disabled={!isInteractive || isUploading || !hasStrokes}
              className="gap-1"
            >
              <Eraser className="size-4" />
              Clear
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSaveSignature}
              disabled={!isInteractive || isUploading || !hasStrokes}
              className="gap-1"
            >
              {isUploading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCcw className="size-4" />}
              {isUploading ? 'Uploading…' : 'Save signature'}
            </Button>
          </div>
        </div>
      ) : null}

      {hasSavedSignature ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-border/70 bg-card p-3">
            <div className="flex flex-col items-center gap-3">
              <img src={signatureUrl} alt="Captured signature" className="max-h-48 w-full object-contain" />
              <p className="text-xs text-muted-foreground">
                {signatureValue?.uploadedAt
                  ? `Captured on ${new Date(signatureValue.uploadedAt).toLocaleString()}`
                  : 'Signature captured'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={clearSignatureValue} disabled={!isInteractive}>
              <RefreshCcw className="size-4" />
              Capture again
            </Button>
            <Button type="button" variant="ghost" size="sm" className="gap-1" asChild>
              <a href={signatureUrl} target="_blank" rel="noreferrer" download>
                <Download className="size-4" />
                Download
              </a>
            </Button>
          </div>
        </div>
      ) : null}

      {question.requireTypedName ? (
        <div className="space-y-1">
          <Input
            value={typedName}
            onChange={handleTypedNameChange}
            onBlur={() => onBlur?.()}
            placeholder="Type your full name"
            disabled={!isInteractive}
            aria-describedby={validationDescriptor}
            aria-errormessage={errorDescriptor}
            aria-invalid={Boolean(error)}
          />
          <p className="text-xs text-muted-foreground">Typed name will appear alongside the captured signature.</p>
        </div>
      ) : null}
    </div>
  );
};
